import { Link } from 'react-router-dom';
import { TriangleAlert } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { t } from '@/i18n';
import type { FinancialMonth, PayrollOverview } from '@ip/shared-types';
import { DashboardSection } from './DashboardSection';
import { PayrollByTypeChart } from './PayrollByTypeChart';
import { PayrollCostStackChart } from './PayrollCostStackChart';
import { PayrollKpiTiles } from './PayrollKpiTiles';
import { PeopleVsInvoiceCostChart } from './PeopleVsInvoiceCostChart';

/**
 * The "Ludzie" band. Rendered only when `payroll` is non-null — a null payroll
 * means the caller may see the financial overview but NOT salaries, which is a
 * permission outcome, not an error: render nothing, say nothing.
 */
export function PeopleSection({
  payroll,
  financialMonths,
  companyNip,
}: {
  payroll: PayrollOverview;
  financialMonths: FinancialMonth[];
  companyNip: string;
}) {
  const { incompleteEmployees } = payroll;

  return (
    <DashboardSection
      title={t('dashboard.people.section')}
      subtitle={t('dashboard.people.sectionSubtitle')}
      action={
        <Link
          to={`/${companyNip}/employees`}
          className="text-muted-foreground hover:text-foreground shrink-0 text-xs font-medium underline underline-offset-4"
        >
          {t('dashboard.people.allEmployees')}
        </Link>
      }
    >
      {/* Not a footnote: these employees' cost is MISSING from every number in
          this section, so the totals below are too low. Status = warning, and a
          status color never carries meaning alone — it ships with an icon and a
          label. */}
      {incompleteEmployees > 0 && (
        <Alert className="border-amber-500/50 text-amber-700 [&>svg]:text-amber-600 dark:text-amber-400 dark:[&>svg]:text-amber-500">
          <TriangleAlert aria-hidden="true" className="size-4" />
          {/* AlertTitle ships with line-clamp-1; this warning must never be
              clipped on a narrow screen — it is the reason the totals are low. */}
          <AlertTitle className="line-clamp-none whitespace-normal">
            {t('dashboard.people.incompleteTitle', { count: incompleteEmployees })}
          </AlertTitle>
          <AlertDescription className="text-amber-700/90 dark:text-amber-400/90">
            <span>
              {t('dashboard.people.incompleteBody')}{' '}
              <Link
                to={`/${companyNip}/employees`}
                className="font-medium underline underline-offset-4"
              >
                {t('dashboard.people.incompleteCta')}
              </Link>
            </span>
          </AlertDescription>
        </Alert>
      )}

      <PayrollKpiTiles kpis={payroll.kpis} />

      <PayrollCostStackChart months={payroll.months} year={payroll.year} />

      <div className="grid gap-4 xl:grid-cols-2">
        <PeopleVsInvoiceCostChart
          payrollMonths={payroll.months}
          financialMonths={financialMonths}
          year={payroll.year}
        />
        <PayrollByTypeChart byType={payroll.byType} />
      </div>
    </DashboardSection>
  );
}
